/* ══════════════════════════════════════════════════════════════
   Market Status Bar — Live ticker bar at top of dashboard
   ══════════════════════════════════════════════════════════════ */

import { getMarketStatus, formatIST, getISTNow, getTimeUntilEvent } from '../utils/ist-time';
import { formatPrice, formatPercent, formatPoints } from '../utils/indian-number';
import { fetchIndices } from '../services/market-data';

export function renderMarketStatusBar(): string {
  return `<div id="market-status-bar-container"></div>`;
}

export function initMarketStatusBar(): void {
  const container = document.getElementById('market-status-bar-container');
  if (!container) return;

  // Render loading state
  container.innerHTML = `
      <div class="status-bar glass-status" style="justify-content: center;">
        <span class="text-muted text-sm">Loading live market status...</span>
      </div>
    `;

  fetchIndices().then(indices => {
    const liveIndices = indices.slice(0, 4).map(idx => ({
      name: idx.name,
      price: idx.last,
      change: idx.change,
      changePct: idx.pChange
    }));

    const status = getMarketStatus();
    const now = getISTNow();
    const timeEvent = getTimeUntilEvent();

    if (liveIndices.length === 0) {
      container.innerHTML = `
        <div class="status-bar glass-status">
          <div class="status-bar__left">
            <span class="market-status market-status--${status.status}">
              <span class="market-status__dot"></span>
              ${status.label}
            </span>
            ${timeEvent ? `<span class="status-bar__timer text-muted text-xs">${timeEvent.label}</span>` : ''}
          </div>
          <div class="status-bar__indices">
            <span class="text-muted text-sm">Live index feed unavailable</span>
          </div>
          <div class="status-bar__right hide-mobile">
            <span class="text-muted text-xs">${formatIST(now, 'time')} IST</span>
          </div>
        </div>
      `;
      return;
    }

    const indicesHTML = liveIndices.map(idx => {
      const isUp = idx.change >= 0;
      const colorClass = idx.name === 'India VIX' || idx.name === 'INDIA VIX'
        ? (idx.change <= 0 ? 'text-bullish' : 'text-bearish')
        : (isUp ? 'text-bullish' : 'text-bearish');

      return `
          <div class="status-bar__index">
            <span class="status-bar__index-name">${idx.name}</span>
            <span class="status-bar__index-price font-mono font-semibold">${formatPrice(idx.price)}</span>
            <span class="status-bar__index-change ${colorClass} font-mono">
              ${formatPoints(idx.change)} (${formatPercent(idx.changePct)})
            </span>
          </div>
        `;
    }).join('');

    container.innerHTML = `
        <div class="status-bar glass-status">
          <div class="status-bar__left">
            <span class="market-status market-status--${status.status}">
              <span class="market-status__dot"></span>
              ${status.label}
            </span>
            ${timeEvent ? `<span class="status-bar__timer text-muted text-xs">${timeEvent.label}</span>` : ''}
          </div>
          <div class="status-bar__indices">
            ${indicesHTML}
          </div>
          <div class="status-bar__right hide-mobile">
            <span class="text-muted text-xs">${formatIST(now, 'time')} IST</span>
          </div>
        </div>
      `;
  });
}

export const MARKET_STATUS_BAR_STYLES = `
  .status-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--space-2) var(--space-4);
    margin-bottom: var(--space-6);
    gap: var(--space-4);
    flex-wrap: wrap;
  }

  .status-bar__left {
    display: flex;
    align-items: center;
    gap: var(--space-3);
  }

  .status-bar__timer {
    font-family: var(--font-mono);
  }

  .status-bar__indices {
    display: flex;
    align-items: center;
    gap: var(--space-6);
    overflow-x: auto;
  }

  .status-bar__index {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    white-space: nowrap;
  }

  .status-bar__index-name {
    font-size: var(--text-xs);
    color: var(--text-muted);
    font-weight: 600;
  }

  .status-bar__index-price {
    font-size: var(--text-sm);
    color: var(--text-primary);
  }

  .status-bar__index-change {
    font-size: var(--text-xs);
  }

  .status-bar__right {
    flex-shrink: 0;
  }
`;
